/**
 * src/pages/Login.jsx — Call It In LLC — Dispatch Authentication
 *
 * Entry point for Call It In operators (dispatchers, drivers, admins).
 * Authentication goes STRICTLY through the live FastAPI backend at
 * https://callitin-backend.onrender.com/auth/login. No local fallback,
 * no hardcoded credentials, no dummy users.
 *
 * Flow:
 *   1. POST /auth/login  →  { access_token, token_type, user?: { id, email, name, role } }
 *   2. Merge token into user object (or build from JWT if user missing)
 *   3. Persist via setCurrentUser()
 *   4. Role-based redirect via shared route policy:
 *      driver → /driver, dispatcher → /dashboard/courier, admin → /dashboards/analytics
 *
 * Note: This dashboard is a standalone Call It In courier platform.
 * AxisSync network integration is available as a connected module for
 * operators who coordinate AxisSync-linked cases.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { setCurrentUser, isAuthenticated, getCurrentRole } from "../data/users";
import { consumeAuthNotice, request } from "../api/client";
import { getDefaultRouteForRole } from "../utils/routeAccess";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Read ?reason= from the URL so the 401 interceptor in client.js can pass a message
  // without needing React Router state (window.location.replace is used for the redirect).
  const sessionNotice = (() => {
    let fallback = null;
    try {
      const reason = new URLSearchParams(window.location.search).get("reason");
      if (reason === "expired") fallback = "Your session has expired. Please sign in again.";
    } catch {
      // ignore query-string parsing failures
    }

    const notice = consumeAuthNotice();
    if (!notice) return fallback;

    const parts = [notice.message || "Your session expired. Please sign in again."];
    if (notice.page) parts.push(`Page: ${notice.page}`);
    if (notice.endpoint) parts.push(`Request: ${notice.method || "GET"} ${notice.endpoint}`);
    return parts.join(" ");
  })();

  // Redirect already-logged-in users to their role-based default (prevents loop)
  useEffect(() => {
    if (isAuthenticated()) {
      const role = getCurrentRole();
      navigate(getDefaultRouteForRole(role), { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Backend: POST /auth/login with JSON body { email, password }
      // (LoginIn schema confirmed via OpenAPI spec — NOT form-encoded)
      const res = await request("/auth/login", {
        method: "POST",
        auth: false,
        body: { email: email.trim().toLowerCase(), password },
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        // Defensive: handle both `access_token` (standard) and `token` (alternative)
        const token = data.access_token || data.token || data.jwt || data.bearer_token;
        if (!token) {
          // Surface the raw response in dev so we can diagnose field name mismatches
          const keys = Object.keys(data).join(", ") || "(empty)";
          setError(`Sign-in failed — server responded but no token was found. Fields received: ${keys}`);
          return;
        }
        // Prefer backend user object; fallback to minimal object for JWT-only responses
        const user = data.user || {};
        const session = {
          id: user.id,
          email: user.email ?? data.email,
          name: user.name ?? user.email ?? "User",
          role: user.role ?? data.role,
          token,
        };
        setCurrentUser(session);
        const resolvedRole = session.role ?? getCurrentRole();
        const destination = getDefaultRouteForRole(resolvedRole);
        navigate(destination, { replace: true });
        return;
      }

      if (res.status === 401) {
        setError("Invalid email or password. Please try again.");
        return;
      }

      const detail = Array.isArray(data?.detail)
        ? data.detail.map((d) => d?.msg ?? d).join(" ")
        : data?.detail;
      setError(detail ?? `Sign-in failed (HTTP ${res.status}). Contact your administrator.`);
    } catch {
      setError(
        "Cannot reach the Call It In dispatch server. " +
          "The backend may be starting up — wait 30 seconds and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440, boxShadow: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
            Call It In Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your credentials to access the dispatch dashboard.
          </Typography>

          {sessionNotice && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {sessionNotice}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              autoComplete="email"
              autoFocus
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              autoComplete="current-password"
              disabled={loading}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ py: 1.25, fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
